import { useRequestGetBulkNotificationCampaigns } from '@/hooks/httpRequest';
import { BulkNotificationCampaignStatusEnum, BulkNotificationCampaignTypeEnum } from '@/enums';
import type { IBulkNotificationCampaign } from '@/interfaces';
import dayjs from 'dayjs';
import 'dayjs/locale/fr';
import { cn } from '@/lib/utils';
import { DataTablePagination } from '@/components/custom';
import { useQueryParams } from '@/hooks';
import { useState } from 'react';
import CampaignDetailModal from './CampaignDetailModal';

const STATUS_STYLES: Record<BulkNotificationCampaignStatusEnum, string> = {
	[BulkNotificationCampaignStatusEnum.PENDING]: 'bg-yellow-100 text-yellow-800',
	[BulkNotificationCampaignStatusEnum.RUNNING]: 'bg-blue-100 text-blue-800',
	[BulkNotificationCampaignStatusEnum.COMPLETED]: 'bg-green-100 text-green-800',
	[BulkNotificationCampaignStatusEnum.CANCELLED]: 'bg-gray-100 text-gray-600',
	[BulkNotificationCampaignStatusEnum.ERROR]: 'bg-red-100 text-red-800',
};

const STATUS_LABELS: Record<BulkNotificationCampaignStatusEnum, string> = {
	[BulkNotificationCampaignStatusEnum.PENDING]: 'En attente',
	[BulkNotificationCampaignStatusEnum.RUNNING]: 'En cours',
	[BulkNotificationCampaignStatusEnum.COMPLETED]: 'Terminée',
	[BulkNotificationCampaignStatusEnum.CANCELLED]: 'Annulée',
	[BulkNotificationCampaignStatusEnum.ERROR]: 'Erreur',
};

const CampaignRow = ({
	campaign,
	idx,
	onClick,
}: {
	campaign: IBulkNotificationCampaign;
	idx: number;
	onClick: () => void;
}) => {
	const scheduledDate =
		campaign.campaignType === BulkNotificationCampaignTypeEnum.ONE_TIME
			? campaign.scheduledAt
			: campaign.startDate;

	return (
		<button
			type="button"
			onClick={onClick}
			className={cn(
				'w-full px-4 py-3 text-left space-y-1 hover:bg-muted/30 transition-colors',
				idx % 2 === 0 && 'bg-muted/5',
			)}
		>
			<div className="flex items-start justify-between gap-2">
				<div className="flex-1 min-w-0">
					<p className="font-medium text-sm truncate">{campaign.title.fr}</p>
					<p className="text-xs text-muted-foreground truncate">{campaign.message.fr}</p>
				</div>
				<span
					className={cn(
						'shrink-0 text-[10px] font-medium px-2 py-0.5 rounded-full leading-4',
						STATUS_STYLES[campaign.status],
					)}
				>
					{STATUS_LABELS[campaign.status]}
				</span>
			</div>

			<div className="flex items-center justify-between text-xs text-muted-foreground">
				<span>
					{campaign.campaignType === BulkNotificationCampaignTypeEnum.ONE_TIME
						? 'Une fois'
						: `Récurrent · ${campaign.intervalDays}j`}
					{scheduledDate && ` · ${dayjs(scheduledDate).locale('fr').format('DD MMMM YYYY HH:mm')}`}
				</span>
				{campaign.totalRuns > 0 && (
					<span>{campaign.totalRuns} envoi{campaign.totalRuns > 1 ? 's' : ''}</span>
				)}
			</div>
		</button>
	);
};

const CampaignsList = () => {
	const { values: [page, limit] } = useQueryParams(['page', 'limit'], { page: 1, limit: 5 });
	const [selected, setSelected] = useState<IBulkNotificationCampaign | null>(null);

	const { campaigns, isLoading, pagination, error } = useRequestGetBulkNotificationCampaigns({
		page: parseInt(String(page)),
		limit: parseInt(String(limit)),
	});

	return (
		<>
			<div className="bg-white rounded-2xl shadow-soft border border-border overflow-hidden">
				<div className="flex items-center justify-between px-4 py-3 border-b border-border">
					<h3 className="font-semibold text-sm">Historique</h3>
					{!isLoading && pagination && (
						<span className="text-xs text-muted-foreground">
							{pagination.itemCount} campagne{pagination.itemCount > 1 ? 's' : ''}
						</span>
					)}
				</div>

				{isLoading ? (
					<div className="divide-y divide-border">
						{Array.from({ length: 5 }).map((_, i) => (
							<div key={i} className={cn('px-4 py-3 space-y-1.5 animate-pulse', i % 2 === 0 && 'bg-muted/5')}>
								<div className="flex items-center justify-between">
									<div className="h-2.5 bg-muted rounded w-32" />
									<div className="h-4 bg-muted rounded w-16" />
								</div>
								<div className="h-3 bg-muted rounded w-4/5" />
								<div className="h-2.5 bg-muted rounded w-1/2" />
							</div>
						))}
					</div>
				) : error ? (
					<p className="px-4 py-10 text-center text-sm text-destructive">Erreur lors du chargement.</p>
				) : campaigns.length === 0 ? (
					<p className="px-4 py-10 text-center text-sm text-muted-foreground">Aucune campagne.</p>
				) : (
					<div className="divide-y divide-border">
						{campaigns.map((c, idx) => (
							<CampaignRow key={c._id} campaign={c} idx={idx} onClick={() => setSelected(c)} />
						))}
					</div>
				)}

				{!isLoading && (pagination?.pageCount ?? 0) > 1 && (
					<div className="px-4 py-3 border-t border-border">
						<DataTablePagination pageCount={pagination?.pageCount} />
					</div>
				)}
			</div>

			<CampaignDetailModal
				open={selected != null}
				campaign={selected}
				onClose={() => setSelected(null)}
			/>
		</>
	);
};

export default CampaignsList;
